import React, { useEffect, useState, useContext, useRef } from "react";
import { useRecoilState } from "recoil";
import { toggleSidebar } from "../../globalState/localData";
import AppLogo from "../../assets/images/appLogoV2.png";
import { Link } from "react-router-dom";
import { getLocalItem } from "../../components/helpers/authService";
import { CartContextData } from "../../context/newsPaperContext";
import { UserDataContext } from "../../context/userContext";
import { PaymentContext } from "../../context/paymentContext";
import NavSearch from "./NavSearch";
import { DisabledItemsContext } from "../../context/DisabledItemsContext";
import { useWallet, naira } from "../Wallet/walletOn";
import "./siteHeader.css";
import { useBillsOn } from "../Bills/billsOn";
import CartToast from "../Cart/CartToast";
import { useInstallKind } from "../Install/InstallPrompt";

// Site header (2026-10-28 redesign): Menu button, logo, section tabs (own swipe row on narrower screens), search, cart and the
// compact account menu. Same actions as before: filterType tabs, cart drawer, wallet when enabled, help, get the app, log out.
const PUBLISH_URL = "https://vendor.newstand.tech/register";
const TABS = [["Today", "all"], ["Newspapers", "Newspaper"], ["Magazines", "Magazines"], ["Books", "Books"], ["Latest", "Latest"], ["Archive", "achive"]];
const I = {
  menu: "M4 7h16M4 12h16M4 17h10",
  search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-3.5-3.5",
  cart: "M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.5L21 8H6M10 20.5h.01M17 20.5h.01",
  library: "M5 4h4v16H5zM10 4h4v16h-4zM15.5 4.5l3.5 1-4 15-3.5-1",
  following: "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21c0-4 3-6 7-6s7 2 7 6M19 8v6M16 11h6",
  subs: "M4 6h16v12H4zM4 10h16",
  payments: "M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6",
  apps: "M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h5",
  gift: "M3 8h18v4H3zM5 12v8h14v-8M12 8v12",
  comments: "M4 5h16v11H8l-4 4z",
  bills: "M13 2L4 14h7l-1 8 9-12h-7l1-8z",
  wallet: "M4 7h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a1 1 0 0 1-1-1V7zM4 7l11-3v3M16 13.5h.01",
};
const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);

export default function Navbar({ browserRouter, routeType, currentRoute }) {
  const billsOn = useBillsOn(); // "Pay bills" only when it is switched on (admin → Bills & profit)
  const wallet = useWallet(); // wallet only when it is switched on for this reader (admin → Wallets)
  const installKind = useInstallKind(); // "Get the app" only where this device can add it and hasn't yet
  const { CartCount, ShowAddToCartModal, onShowAddToCartModalModal, cartDrawerOpen, setfilterType, filterType } = useContext(CartContextData);
  const { OpenWalletModal } = useContext(PaymentContext);
  const { UserInfo, LogUserOut, userLocation } = useContext(UserDataContext);
  const { showReview } = useContext(DisabledItemsContext);
  const [sidebarState, setSidebarState] = useRecoilState(toggleSidebar);

  const [signedIn, setSignedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [accOpen, setAccOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const accRef = useRef(null);
  const avatarRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setSignedIn(false); return; }
    setSignedIn(true);
    try { setUserData(JSON.parse(getLocalItem("user"))); } catch (e) { setUserData(null); }
  }, [UserInfo]);

  // Account menu: closes on a click outside, Esc, or when the page changes.
  useEffect(() => {
    if (!accOpen) return undefined;
    const onDown = (e) => {
      if (accRef.current && accRef.current.contains(e.target)) return;
      if (avatarRef.current && avatarRef.current.contains(e.target)) return;
      setAccOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") { setAccOpen(false); if (avatarRef.current) avatarRef.current.focus(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [accOpen]);
  useEffect(() => { setAccOpen(false); setSearchOpen(false); }, [currentRoute]);

  const openMenu = () => setSidebarState({ ...(sidebarState || {}), openSidebar: true, defaultSidebarApiState: "AppCloseSidebar" });
  const go = (path) => { setAccOpen(false); browserRouter(path); };
  const onHome = currentRoute === "/" || currentRoute === "/home";
  const tabOn = (filter) => (filter === "achive" ? /^\/archive/.test(currentRoute || "") : onHome && filterType === filter);
  const pickTab = (filter) => {
    setfilterType(filter);
    if (filter === "achive") browserRouter("/archive");
    else browserRouter("/");
  };

  const avatar = UserInfo && UserInfo.avatar ? UserInfo.avatar : null;
  const first = (userData && userData.firstName) || "";
  const last = (userData && userData.lastName) || "";
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "Me";
  const cartN = Number(CartCount) > 0 ? Number(CartCount) : 0;
  const showTabs = routeType !== "pdf";

  const tabs = (
    <nav className="sh-tabs" aria-label="Sections">
      {TABS.map(([label, filter]) => (
        <button type="button" key={filter} className={`sh-tab${tabOn(filter) ? " on" : ""}`} aria-current={tabOn(filter) ? "page" : undefined} onClick={() => pickTab(filter)}>{label}</button>
      ))}
    </nav>
  );

  return (
    <React.Fragment>
      <header className="sh">
        <div className="sh-bar">
          <button type="button" className="sh-menu" onClick={openMenu} aria-label="Open the menu"><Icon d={I.menu} /><span>Menu</span></button>
          <button type="button" className="sh-logo" onClick={() => { setfilterType("all"); browserRouter("/"); }} aria-label="TheNewspaperStand home">
            <img src={AppLogo} alt="TheNewspaperStand" />
          </button>
          {showTabs ? tabs : null}
          <div className="sh-right">
            <NavSearch browserRouter={browserRouter} isLoggedIn={signedIn} />
            <button type="button" className="sh-icon sh-only-phone" aria-label="Search papers and stories" aria-expanded={searchOpen} onClick={() => setSearchOpen((v) => !v)}><Icon d={I.search} /></button>
            {signedIn ? (
              <React.Fragment>
                {wallet.enabled ? (
                  <button type="button" className={`sh-wallet${wallet.low ? " low" : ""}`} onClick={OpenWalletModal} aria-label={`Wallet, balance ${naira(wallet.balance)}${wallet.low ? ", running low" : ""}`} title={wallet.low ? "Running low" : undefined}><Icon d={I.wallet} size={17} />{naira(wallet.balance)}</button>
                ) : null}
                <button type="button" className="sh-icon sh-cart" onClick={onShowAddToCartModalModal} aria-label={cartN ? `My cart, ${cartN} item${cartN === 1 ? "" : "s"}` : "My cart"}>
                  <Icon d={I.cart} />{cartN ? <span className="sh-badge" aria-hidden="true">{cartN}</span> : null}
                </button>
                <button type="button" ref={avatarRef} className="sh-avatar" aria-haspopup="menu" aria-expanded={accOpen} aria-label="Your account menu" onClick={() => setAccOpen((v) => !v)}>
                  {avatar ? <img src={avatar} alt="" /> : initials}
                </button>
              </React.Fragment>
            ) : (
              <div className="sh-out">
                <a className="sh-link" href={PUBLISH_URL} target="_blank" rel="noreferrer">Publish your content</a>
                <Link className="sh-link" to="/login">Log in</Link>
                <Link className="sh-cta" to="/create_account">Sign up</Link>
              </div>
            )}
          </div>
        </div>
        <div className={`sh-msearch${searchOpen ? " open" : ""}`}>{searchOpen ? <NavSearch browserRouter={browserRouter} isLoggedIn={signedIn} /> : null}</div>
        {showTabs ? <div className="sh-row">{tabs}</div> : null}

        {signedIn && accOpen ? (
          <nav className="sh-acc" ref={accRef} aria-label="Your account">
            <div className="sh-acc-me">
              <span className="sh-avatar" aria-hidden="true">{avatar ? <img src={avatar} alt="" /> : initials}</span>
              <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span className="sh-acc-name">{`${first} ${last}`.trim() || "Your account"}</span>
                <button type="button" className="sh-acc-link" onClick={() => go("/account")}>My account ›</button>
              </span>
            </div>
            <div className="sh-tiles">
              <button type="button" className="sh-tile" onClick={() => go("/account/library")}><Icon d={I.library} size={20} />Library</button>
              <button type="button" className="sh-tile" onClick={() => go("/account/library?tab=following")}><Icon d={I.following} size={20} />Following</button>
              <button type="button" className="sh-tile" onClick={() => go("/account/subscriptions")}><Icon d={I.subs} size={20} />Subscriptions</button>
              <button type="button" className="sh-tile" onClick={() => go("/account/payments")}><Icon d={I.payments} size={20} />Payments</button>
              <button type="button" className="sh-tile" onClick={() => go("/account/applications")}><Icon d={I.apps} size={20} />Applications</button>
              <button type="button" className="sh-tile" onClick={() => { setAccOpen(false); onShowAddToCartModalModal(); }} aria-label={cartN ? `Cart, ${cartN} item${cartN === 1 ? "" : "s"}` : "Cart"}>
                <Icon d={I.cart} size={20} />Cart{cartN ? <span className="sh-badge" aria-hidden="true">{cartN}</span> : null}
              </button>
            </div>
            <div className="sh-rows">
              <button type="button" className="sh-rowi" onClick={() => go("/account/gifts")}><Icon d={I.gift} /><span>Gifts for others</span></button>
              {showReview ? <button type="button" className="sh-rowi" onClick={() => go("/reviews")}><Icon d={I.comments} /><span>Your comments</span></button> : null}
              {wallet.enabled ? <button type="button" className="sh-rowi" onClick={() => { setAccOpen(false); OpenWalletModal(); }}><Icon d={I.wallet} /><span>Wallet</span><em className="wl-em">{naira(wallet.balance)}</em></button> : null}
              {billsOn ? <button type="button" className="sh-rowi" onClick={() => go("/account/bills")}><Icon d={I.bills} /><span>Pay bills</span><em>NEW</em></button> : null}
            </div>
            <div className="sh-foot">
              <button type="button" onClick={() => go("/account/settings")}>Settings</button>
              <button type="button" onClick={() => { setAccOpen(false); window.dispatchEvent(new Event("tns:help-open")); }}>Help</button>
              {installKind ? <button type="button" onClick={() => { setAccOpen(false); window.dispatchEvent(new Event("tns:install-open")); }}>Get the app</button> : null}
              <button type="button" className="sh-logout" onClick={LogUserOut}>Log out</button>
            </div>
          </nav>
        ) : null}
      </header>

      <ShowAddToCartModal />
      <CartToast count={CartCount} drawerOpen={cartDrawerOpen} onView={onShowAddToCartModalModal} />
    </React.Fragment>
  );
}
